import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon, Monitor } from 'lucide-react';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppVersion } from '@/hooks/useAppVersion';
import { useAuth } from '@/contexts/AuthContext';
import { COURSE_ABOUT_HREF, COURSE_HOW_HREF, MARKETING_CTAS } from '@/lib/marketingCtas';

type ThemeMode = 'system' | 'light' | 'dark';

function getTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = localStorage.getItem('mentara_theme');
  if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  return 'system';
}

function applyTheme(theme: ThemeMode) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // System preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

export function Footer() {
  const [theme, setTheme] = useState<ThemeMode>(() => getTheme());
  const appInfo = useAppVersion();
  const { isAuthenticated, user } = useAuth();
  const currentYear = new Date().getFullYear();
  // Parents only have a dashboard, calendar and settings. Linking to Sessions or
  // Messages would bounce them back to /parent.
  const appLinks = user?.role === 'PARENT'
    ? [
        { to: '/parent', label: 'Dashboard' },
        { to: '/calendar', label: 'Calendar' },
        { to: '/settings', label: 'Settings' },
      ]
    : [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/sessions', label: 'Sessions' },
        { to: '/calendar', label: 'Calendar' },
        { to: '/chats', label: 'Messages' },
        { to: '/settings', label: 'Settings' },
      ];

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('mentara_theme', theme);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('theme-change', { detail: theme }));
  }, [theme]);

  // Listen for theme changes from other components
  useEffect(() => {
    const handleThemeChange = (e: CustomEvent<ThemeMode>) => {
      setTheme(e.detail);
    };
    window.addEventListener('theme-change', handleThemeChange as EventListener);
    return () => window.removeEventListener('theme-change', handleThemeChange as EventListener);
  }, []);

  // Listen for storage changes to sync theme across tabs
  useEffect(() => {
    const handleStorage = () => {
      const newTheme = getTheme();
      setTheme(newTheme);
      applyTheme(newTheme);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Listen for system preference changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme('system');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const themeSelect = (
    <Select value={theme} onValueChange={(value: ThemeMode) => setTheme(value)}>
      <SelectTrigger className="h-8 w-[120px] text-xs" aria-label="Choose theme">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="system"><div className="flex items-center gap-2"><Monitor className="h-4 w-4" />System</div></SelectItem>
        <SelectItem value="light"><div className="flex items-center gap-2"><Sun className="h-4 w-4" />Light</div></SelectItem>
        <SelectItem value="dark"><div className="flex items-center gap-2"><Moon className="h-4 w-4" />Dark</div></SelectItem>
      </SelectContent>
    </Select>
  );

  if (isAuthenticated) {
    return (
      <footer className="mt-auto border-t border-border bg-background/50 backdrop-blur-sm" data-testid="app-footer">
        {/* Bottom padding keeps the floating chat and assistant buttons off the legal line. */}
        <div className="container space-y-3 pb-20 pt-5 text-sm text-muted-foreground">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <nav aria-label="App" className="flex flex-wrap gap-x-5 gap-y-2">
              {appLinks.map((l) => (
                <Link key={l.to} to={l.to} className="transition-colors hover:text-foreground">{l.label}</Link>
              ))}
              <a href="mailto:admin@mentaratutors.com" className="transition-colors hover:text-foreground">Contact us</a>
            </nav>
            {themeSelect}
          </div>
          <div className="flex flex-col gap-2 border-t border-border pt-3 md:pr-20 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <p>© {currentYear} Mentara</p>
              {appInfo && <span className="text-xs opacity-70">v{appInfo.version}</span>}
            </div>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="transition-colors hover:text-foreground">Terms of service</Link>
              <Link to="/privacy" className="transition-colors hover:text-foreground">Privacy policy</Link>
            </div>
          </div>
          {/* Required by College Board's trademark guidelines whenever the site names the SAT or Bluebook. */}
          <p className="text-xs text-muted-foreground/80" data-testid="footer-trademark">
            SAT® and Bluebook™ are trademarks registered or owned by the College Board, which is not affiliated with, and does not endorse, this site.
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border bg-background/50 backdrop-blur-sm mt-auto">
      <div className="container py-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-12">
          {/* Brand */}
          <div className="col-span-2 space-y-3 md:col-span-4">
            <div className="flex items-center gap-3">
              <MantaRayIcon className="h-9 w-9 object-contain" />
              <span className="font-bold text-rose-900 dark:text-rose-300">Mentara</span>
            </div>
            <p className="max-w-xs text-sm text-muted-foreground">
              The free SAT course, plus 1-on-1 tutoring when you want it.
            </p>
          </div>

          {/* Course */}
          <nav aria-label="Course" className="space-y-3 md:col-span-2">
            <h4 className="font-semibold text-sm">Course</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to={MARKETING_CTAS.closeCourse.href} className="font-medium text-primary transition-colors hover:underline dark:text-rose-300">
                  Start the free SAT{' '}
                  <span className="whitespace-nowrap">
                    course
                    <span aria-hidden="true" className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle dark:bg-rose-400" />
                  </span>
                </Link>
              </li>
              <li><Link to={COURSE_HOW_HREF} className="hover:text-foreground transition-colors">How the course works</Link></li>
              <li><Link to={COURSE_ABOUT_HREF} className="hover:text-foreground transition-colors">Inside the course</Link></li>
            </ul>
          </nav>

          {/* Mentara */}
          <nav aria-label="Mentara" className="space-y-3 md:col-span-2">
            <h4 className="font-semibold text-sm">Mentara</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/what-we-do/students/" className="hover:text-foreground transition-colors">For students</Link></li>
              <li><Link to="/what-we-do/parents/" className="hover:text-foreground transition-colors">For parents</Link></li>
              <li><Link to="/what-we-do/tutors/" className="hover:text-foreground transition-colors">For tutors</Link></li>
              <li><Link to="/about" className="hover:text-foreground transition-colors">About</Link></li>
              <li><Link to="/blog" className="hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link to="/faq" className="hover:text-foreground transition-colors">FAQ</Link></li>
            </ul>
          </nav>

          {/* Account */}
          <nav aria-label="Account" className="space-y-3 md:col-span-2">
              <h4 className="font-semibold text-sm">Account</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to={MARKETING_CTAS.login.href} className="hover:text-foreground transition-colors">{MARKETING_CTAS.login.label}</Link></li>
                <li><Link to={MARKETING_CTAS.createAccount.href} className="hover:text-foreground transition-colors">{MARKETING_CTAS.createAccount.label}</Link></li>
                <li><a href="mailto:admin@mentaratutors.com" className="hover:text-foreground transition-colors">Contact us</a></li>
              </ul>
            </nav>

          {/* Theme Toggle */}
          <div className="space-y-3 md:col-span-2">
            <h4 className="font-semibold text-sm">Appearance</h4>
            <Select value={theme} onValueChange={(value: ThemeMode) => setTheme(value)}>
              <SelectTrigger className="w-[140px]" aria-label="Choose theme">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    System
                  </div>
                </SelectItem>
                <SelectItem value="light">
                  <div className="flex items-center gap-2">
                    <Sun className="h-4 w-4" />
                    Light
                  </div>
                </SelectItem>
                <SelectItem value="dark">
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4" />
                    Dark
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
            <p>© {currentYear} Mentara. All rights reserved.</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          </div>
        </div>
        {/* Required by College Board's trademark guidelines whenever the site names the SAT or Bluebook. */}
        <p className="mt-4 text-center text-xs text-muted-foreground/80 md:text-left" data-testid="footer-trademark">
          SAT® and Bluebook™ are trademarks registered or owned by the College Board, which is not affiliated with, and does not endorse, this site.
        </p>
      </div>
    </footer>
  );
}
